import { X } from 'lucide-react';
import { useState, type ChangeEvent, type FormEvent } from 'react';

import type { ApiClient, TrackDto } from '../api';
import { errorMessage } from './format';

type TrackEditorProps = {
  track: TrackDto;
  api: ApiClient;
  onClose(): void;
  onSaved(): void;
};

export function TrackEditor({ track, api, onClose, onSaved }: TrackEditorProps) {
  const [title, setTitle] = useState(track.title);
  const [artist, setArtist] = useState(track.artist ?? '');
  const [album, setAlbum] = useState(track.album ?? '');
  const [genre, setGenre] = useState(track.genre ?? '');
  const [description, setDescription] = useState(track.description ?? '');
  const [year, setYear] = useState(track.recordedYear?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.updateTrack(track.id, {
        title,
        artist,
        album,
        genre,
        description,
        recordedYear: year ? Number(year) : null,
      });
      onSaved();
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  }

  async function changeCover(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const upload = await api.createCoverUpload(track.id, {
        contentType: file.type,
        sizeBytes: file.size,
      });
      const response = await fetch(upload.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!response.ok) throw new Error('Cover upload failed');
      await api.completeCoverUpload(track.id, upload.id);
      onSaved();
    } catch (reason) {
      setError(errorMessage(reason));
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="edit-title">
        <header>
          <h2 id="edit-title">Edit set</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close editor">
            <X size={17} />
          </button>
        </header>
        <form onSubmit={submit}>
          <label className="field">
            <span className="label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              maxLength={200}
            />
          </label>
          <div className="form-grid">
            <label className="field">
              <span className="label">Artist</span>
              <input type="text" value={artist} onChange={(event) => setArtist(event.target.value)} />
            </label>
            <label className="field">
              <span className="label">Album</span>
              <input type="text" value={album} onChange={(event) => setAlbum(event.target.value)} />
            </label>
            <label className="field">
              <span className="label">Genre</span>
              <input type="text" value={genre} onChange={(event) => setGenre(event.target.value)} />
            </label>
            <label className="field">
              <span className="label">Year</span>
              <input
                type="number"
                min="1000"
                max="9999"
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </label>
          </div>
          <label className="field">
            <span className="label">Description</span>
            <textarea
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <label className="cover-field">
            <span className="label">Replace cover</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={changeCover}
            />
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <footer>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button className="button primary" disabled={busy}>
              {busy ? 'Saving' : 'Save'}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
